import React from "react";
import { Currency } from "../types/enums";

interface VirtualKeyboardProps {
  value: string;
  onValueChange: (value: string) => void;
  onClose?: () => void;
  currency?: string;
  chips?: ChipItem[];
}

const VirtualKeyboard: React.FC<VirtualKeyboardProps> = ({
  value,
  onValueChange,
  onClose,
  currency = Currency.UGX,
  chips = [
    { action: "clear", value: 0, label: "Clear" },
    { action: "add", value: 5, label: "+5" },
    { action: "add", value: 10, label: "+10" },
    { action: "add", value: 50, label: "+50" },
  ],
}) => {
  const handleChipClick = (chip: ChipItem) => {
    if (chip.action === "clear") {
      onValueChange("");
    } else {
      const currentValue = parseFloat(value) || 0;
      const newValue = currentValue + chip.value;
      onValueChange(newValue.toString());
    }
  };

  const handleNumberClick = (num: string) => {
    onValueChange(value + num);
  };

  const handleDotClick = () => {
    if (!value.includes(".")) {
      onValueChange(value + ".");
    }
  };

  const handleDeleteClick = () => {
    onValueChange(value.slice(0, -1));
  };

  const handleOkClick = () => {
    if (onClose) {
      onClose();
    }
  };

  // Base button styles - prevents text selection, optimizes for touch
  const baseButtonStyles = {
    WebkitUserSelect: "none" as const,
    MozUserSelect: "none" as const,
    msUserSelect: "none" as const,
    userSelect: "none" as const,
    WebkitTouchCallout: "none" as const,
    touchAction: "manipulation" as const,
    WebkitTapHighlightColor: "transparent",
  };

  return (
    <div
      className="w-full flex flex-col gap-2.5 px-4 pb-3.5 bg-keyboard-bg select-none"
      style={{
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
        touchAction: "manipulation",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      {/* Chips Row */}
      <div className="flex gap-2.5">
        {chips.map((chip, index) => (
          <button
            key={index}
            onClick={() => handleChipClick(chip)}
            style={baseButtonStyles}
            className="flex-1 bg-keyboard-chip-bg text-keyboard-chip-text border border-keyboard-chip-border font-bold h-[38px] px-4 rounded-4 cursor-pointer outline-none text-sm"
          >
            {chip.label}
          </button>
        ))}
      </div>

      {/* Number Pad - flex with gap 3px */}
      <div className="flex gap-[3px]">
        <div className="flex flex-col gap-[3px] flex-1">
          <div className="flex gap-[3px]">
            {["1", "2", "3", "4", "5"].map((num) => (
              <button
                key={num}
                onClick={() => handleNumberClick(num)}
                style={baseButtonStyles}
                className="flex-1 bg-keyboard-number-bg text-keyboard-number-text font-bold h-[38px] px-4 active:scale-95 cursor-pointer outline-none border-none text-sm"
              >
                {num}
              </button>
            ))}
            <button
              onClick={handleDotClick}
              style={baseButtonStyles}
              className="flex-1 bg-keyboard-number-bg text-keyboard-number-text font-bold h-[38px] px-4 active:scale-95 cursor-pointer outline-none border-none text-sm"
            >
              .
            </button>
          </div>
          <div className="flex gap-[3px]">
            {["6", "7", "8", "9", "0"].map((num) => (
              <button
                key={num}
                onClick={() => handleNumberClick(num)}
                style={baseButtonStyles}
                className="flex-1 bg-keyboard-number-bg text-keyboard-number-text font-bold h-[38px] px-4 active:scale-95 cursor-pointer outline-none border-none text-sm"
              >
                {num}
              </button>
            ))}
            <button
              onClick={handleDeleteClick}
              style={baseButtonStyles}
              className="flex-1 bg-keyboard-number-bg text-keyboard-number-text font-bold h-[38px] px-4 active:scale-95 cursor-pointer outline-none border-none text-sm"
            >
              <i className="au-i-delete-button"></i>
            </button>
          </div>
        </div>
        {onClose && (
          <button
            onClick={handleOkClick}
            style={baseButtonStyles}
            className="self-stretch bg-keyboard-number-bg text-keyboard-number-text font-bold px-4 active:scale-95 cursor-pointer outline-none border-none min-w-[48px]"
          >
            OK
          </button>
        )}
      </div>
    </div>
  );
};

export default VirtualKeyboard;
